import i18n from "i18next";
import {
	CustomerOrderDataGridApi,
	CustomerOrderDataGridApi_CustomerOrderDto,
} from "src/api/generated/erp/sales/customerOrder/api/customerOrderDataGridApi.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dateColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { ServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel.tsx";
import { getCustomerOrderStateLabels } from "src/api/generated/erp/db/types/enums/customerOrderState.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { CustomerOrdersPage } from "src/components/views/erp/sales/customerOrder/CustomerOrdersPage.tsx";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesLeadCustomerOrdersViewProps {
	salesLeadId: number;
}

export const SalesLeadCustomerOrdersView = ({ salesLeadId }: SalesLeadCustomerOrdersViewProps) => {
	const { openDialog } = useGenericDialog();
	return (
		<ServerSideDataGridModel
			gridId={"D09C7E4B12F85A63"}
			fetchData={({ dataModelRequest }) =>
				CustomerOrderDataGridApi.getSalesLeadCustomerOrders({ dataModelRequest, salesLeadId })
			}
			getDataModelResult={(response) => response}
			initialParams={{}}
			getRowId={(row) => row.customerOrderId}
			render={({ dataGridProps }) => (
				<AavoDataGrid<CustomerOrderDataGridApi_CustomerOrderDto>
					disableMultipleRowSelection
					columns={[
						integerColumn({
							field: "customerOrderId",
							headerName: i18n.t("number_shortened"),
						}),
						textColumn({
							field: "siteName",
							headerName: i18n.t("site_short"),
						}),
						textColumn({
							field: "customerName",
							headerName: i18n.t("customer"),
							width: 180,
						}),
						textColumn({
							field: "orderReference",
							headerName: i18n.t("reference"),
							width: 180,
						}),
						textColumn({
							field: "customerPoNo",
							headerName: i18n.t("customer_po_no"),
						}),
						enumColumn({
							field: "customerOrderState",
							headerName: i18n.t("state"),
							enumLabels: getCustomerOrderStateLabels(),
						}),
						dateColumn({
							field: "plannedDeliveryDate",
							headerName: i18n.t("planned_delivery_date_short"),
						}),
						dateColumn({
							field: "contractDate",
							headerName: i18n.t("contract_date"),
						}),
						textColumn({
							field: "responsiblePersonName",
							headerName: i18n.t("responsible_person"),
							width: 180,
						}),
						textColumn({
							field: "salespersonName",
							headerName: i18n.t("salesperson"),
							width: 180,
						}),
					]}
					rowContextMenuComponents={({ row }) => (
						<OpenCustomerOrderButton key={"openCustomerOrder"} customerOrderId={row.customerOrderId} />
					)}
					openRow={openCustomerOrder}
					listViewOnMobile
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.crm.salesLeadCustomerOrders.grid"),
						data: { salesLeadId },
						selectedRowPathSegment: (row) => `${i18n.t("customer_order")} ${row.customerOrderId}`,
						selectedRowsPathSegment: (count) => i18n.t("customer_orders_selected_count", { count }),
						rowFields: ["customerOrderId", "salesLeadId", "customerId", "customerOrderOfferId", "orderReference"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);

	function openCustomerOrder(row: CustomerOrderDataGridApi_CustomerOrderDto) {
		openDialog({
			title: i18n.t("customer_order"),
			content: <CustomerOrdersPage onlyCustomerOrderToShowId={row.customerOrderId} />,
			agentContext: {
				key: defineAgentUiContextKey("erp.sales.customerOrder.salesLeadCustomerOrdersView.dialog"),
				title: i18n.t("customer_order"),
				pathSegments: [`${i18n.t("customer_order")} ${row.customerOrderId}`],
				data: {
					customerOrderId: row.customerOrderId,
					salesLeadId,
				},
			},
		});
	}
};
